import React, { useState, useEffect } from 'react';
import { ethers, parseUnits } from 'ethers';
import './MarketDetail.css';
import { AroundUIHelperContract } from '../contracts/AroundUIHelper';
import { useEthers } from '../context/EthersContext';

const MintTestPage = ({ signerOverride }) => {
  const [token, setToken] = useState('');
  const [amount, setAmount] = useState('100');
  const [recipient, setRecipient] = useState('');
  const [decimals, setDecimals] = useState(18);
  const [balance, setBalance] = useState('0');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const { signer: ctxSigner, provider: ctxProvider, addresses: ctxAddrs } = useEthers();

  useEffect(() => {
    const init = async () => {
      try {
        const signer = signerOverride || ctxSigner;
        if (signer) {
          const addr = await signer.getAddress();
          setRecipient(addr);
        }
      } catch (e) { console.error('init recipient failed', e); }
      try {
        const t = (ctxAddrs?.USDC || '');
        setToken(t);
      } catch (e) { console.error('init token failed', e); }
    };
    init();
  }, [ctxAddrs]);

  useEffect(() => {
    const auto = async () => {
      if (!token) return;
      await refreshDecimals();
      await checkBalance();
    };
    auto();
  }, [token, recipient, ctxProvider, ctxSigner]);

  const refreshDecimals = async () => {
    try {
      const { AroundUIHelper } = ctxAddrs || {};
      const provider = ctxProvider;
      const ui = new AroundUIHelperContract(AroundUIHelper, provider);
      const d = await ui.getDecimals(token);
      setDecimals(Number(d) || 6);
    } catch (e) { console.error('refreshDecimals failed', e); }
  };

  const checkBalance = async () => {
    try {
      setMessage('');
      const signer = signerOverride || ctxSigner;
      if (!signer) throw new Error('Please connect wallet');
      const addr = recipient || (await signer.getAddress());
      const { AroundUIHelper, chainId: cfgChainId } = ctxAddrs || {};
      const provider = ctxProvider;
      const net = await provider.getNetwork().catch(() => null);
      const pChain = Number(net?.chainId);
      if (!Number.isFinite(pChain) || !Number.isFinite(Number(cfgChainId)) || pChain !== Number(cfgChainId)) throw new Error('Network mismatch');
      const code = await provider.getCode(token).catch(() => '0x');
      if (!code || code === '0x') throw new Error('Token not deployed on this network');
      const ui = new AroundUIHelperContract(AroundUIHelper, provider);
      const d = await ui.getDecimals(token);
      const dec = Number(d) || 6;
      setDecimals(dec);
      const bal = await ui.getTokenBalance(token, addr);
      setBalance(ethers.formatUnits(bal ?? 0n, dec));
    } catch (e) {
      setMessage(e?.message || 'Balance check failed');
    }
  };

  const tryMint = async () => {
    try {
      setMessage('');
      setLoading(true);
      const signer = signerOverride || ctxSigner;
      if (!signer) throw new Error('Please connect wallet');
      const net = await ctxProvider.getNetwork().catch(() => null);
      const pChain = Number(net?.chainId);
      const cfgChainId = Number(ctxAddrs?.chainId);
      if (!Number.isFinite(pChain) || !Number.isFinite(cfgChainId) || pChain !== cfgChainId) throw new Error('Network mismatch');
      const code = await ctxProvider.getCode(token).catch(() => '0x');
      if (!code || code === '0x') throw new Error('Token not deployed');
      const dec = decimals || 6;
      const amt = parseUnits(String(amount || '0'), dec);
      if (amt <= 0n) throw new Error('Enter a positive amount');
      const to = recipient || (await signer.getAddress());
      const c1 = new ethers.Contract(token, ["function mint(address to, uint256 amount)"], signer);
      try {
        const tx = await c1.mint(to, amt);
        await tx.wait();
        setMessage('Mint successful');
        await checkBalance();
        setLoading(false);
        return;
      } catch (e) { console.error('mint(address,uint256) failed', e); }
      const c2 = new ethers.Contract(token, ["function faucet(address to, uint256 amount)"], signer);
      try {
        const tx = await c2.faucet(to, amt);
        await tx.wait();
        setMessage('Mint successful');
        await checkBalance();
        setLoading(false);
        return;
      } catch (e) { console.error('faucet(address,uint256) failed', e); }
      const c3 = new ethers.Contract(token, ["function mint(uint256 amount)"], signer);
      try {
        const tx = await c3.mint(amt);
        await tx.wait();
        setMessage('Mint successful');
        await checkBalance();
        setLoading(false);
        return;
      } catch (e) { console.error('mint(uint256) failed', e); }
      const c4 = new ethers.Contract(token, ["function faucet(uint256 amount)"], signer);
      try {
        const tx = await c4.faucet(amt);
        await tx.wait();
        setMessage('Mint successful');
        await checkBalance();
        setLoading(false);
        return;
      } catch (e) { console.error('faucet(uint256) failed', e); }
      setLoading(false);
      setMessage('Mint failed. Token may not expose public mint/faucet');
    } catch (e) {
      setLoading(false);
      setMessage(e?.message || 'Mint failed');
    }
  };

  return (
    <div className="market-detail-container">
      <div className="back-nav"><a className="back-link" href="/">← Back to Markets</a></div>
      <div className="market-detail-card">
        <div className="market-header">
          <h1>Faucet</h1>
          <div className="market-meta">
            <span className="market-end">Decimals: {decimals}</span>
            <span className="market-end">Balance: {balance}</span>
          </div>
        </div>
        <div className="trade-section">
          <div className="trade-card">
              <div className="trade-header">
                <div className="trade-title">Faucet Token</div>
              </div>
            <div className="trade-form">
              <div className="input-group">
                <label htmlFor="token">Token Address</label>
                <input id="token" type="text" value={token} readOnly />
              </div>
              <div className="input-group">
                <label htmlFor="recipient">Recipient</label>
                <input id="recipient" type="text" placeholder="0x..." value={recipient} onChange={(e) => setRecipient(e.target.value)} />
              </div>
              <div className="input-group">
                <label htmlFor="amount">Amount</label>
                <input id="amount" type="number" min="0" step="0.0001" placeholder="100" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={refreshDecimals}>Refresh Decimals</button>
                <button className="cancel-btn" onClick={checkBalance}>Check Balance</button>
                <button className="confirm-btn" onClick={tryMint} disabled={loading}>Mint</button>
              </div>
              {message && <div className="message">{message}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MintTestPage;
